/**
 * A library audio row as a card: its waveform, and a click to hear it. A sample nobody can audition is
 * a filename, and every other card in Studio plays on click, so this one does too (Dax, 2026-09-20).
 *
 * One <audio> element is shared by the whole grid, through useAudition: a hundred cards must not mean
 * a hundred media elements, and only one sound should ever play at a time.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { Icon } from './icons';

/** The one player the grid shares. Returns what is playing, and a toggle. */
export function useAudition(): { playingId: string | null; toggle: (id: string, url: string | null) => void; warm: (url: string | null) => void; stop: () => void } {
  const [playingId, setPlayingId] = useState<string | null>(null);
  const elRef = useRef<HTMLAudioElement | null>(null);

  const warmRef = useRef<HTMLAudioElement | null>(null);

  const stop = useCallback(() => {
    elRef.current?.pause();
    setPlayingId(null);
  }, []);

  /**
   * Start fetching on hover, so the click plays rather than waits. Measured 2026-09-20: the round trip
   * to the bucket is about 0.55 s before a byte arrives, and a hover is worth more than that. One warm
   * element, reused, so passing the cursor over a grid does not open a hundred connections.
   */
  const warm = useCallback((url: string | null) => {
    if (!url) return;
    let el = warmRef.current;
    if (!el) {
      el = new Audio();
      el.preload = 'auto';
      warmRef.current = el;
    }
    if (el.src !== url) el.src = url;
  }, []);

  const toggle = useCallback((id: string, url: string | null) => {
    if (!url) return;
    let el = elRef.current;
    if (!el) {
      el = new Audio();
      el.addEventListener('ended', () => setPlayingId(null));
      el.addEventListener('error', () => setPlayingId(null));
      elRef.current = el;
    }
    if (playingId === id) {
      el.pause();
      setPlayingId(null);
      return;
    }
    if (el.src !== url) el.src = url;
    el.currentTime = 0;
    void el.play().then(() => setPlayingId(id)).catch(() => setPlayingId(null));
  }, [playingId]);

  useEffect(() => () => {
    elRef.current?.pause();
    elRef.current = null;
    warmRef.current = null;
  }, []);
  return { playingId, toggle, warm, stop };
}

/**
 * The waveform, drawn from the bars the library stored at ingest. A row with none (an old one, or a
 * file that would not decode) gets a flat placeholder rather than an empty box, so the card still
 * reads as audio.
 */
export function AudioWave({ peaks, playing }: { peaks: number[]; playing: boolean }) {
  const ref = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const canvas = ref.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;
    const w = canvas.clientWidth;
    const h = canvas.clientHeight;
    if (!w || !h) return;
    const dpr = window.devicePixelRatio || 1;
    canvas.width = Math.round(w * dpr);
    canvas.height = Math.round(h * dpr);
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, w, h);
    const bars = peaks.length ? peaks : new Array(48).fill(0.06);
    const step = w / bars.length;
    const barW = Math.max(1, step * 0.62);
    ctx.fillStyle = playing ? 'rgba(37, 99, 235, 0.9)' : 'rgba(0, 0, 0, 0.32)';
    for (const [i, p] of bars.entries()) {
      const barH = Math.max(2, Math.min(1, p) * (h - 4));
      ctx.fillRect(i * step + (step - barW) / 2, (h - barH) / 2, barW, barH);
    }
  }, [peaks, playing]);
  return <canvas ref={ref} className="audio-wave" role="img" aria-label="Waveform" />;
}

export function AudioCardMedia({ peaks, playing, name, onToggle, onWarm, busy }: {
  peaks: number[];
  playing: boolean;
  name: string;
  onToggle: () => void;
  /** Still being measured or named: a light swims across the waveform until it settles. */
  busy?: boolean;
  /** Hovering starts the fetch, so the click plays instead of waiting for the bucket. */
  onWarm?: () => void;
}) {
  return (
    <button type="button" className={`audio-media${playing ? ' on' : ''}`} onClick={onToggle}
      onMouseEnter={onWarm} onFocus={onWarm}
      title={playing ? `Stop ${name}` : `Play ${name}`} aria-label={playing ? `Stop ${name}` : `Play ${name}`}>
      <AudioWave peaks={peaks} playing={playing} />
      {busy && <span className="audio-busy" role="status" aria-label="Still being measured and named" />}
      <span className="audio-play" aria-hidden="true"><Icon name={playing ? 'pause' : 'play'} /></span>
    </button>
  );
}
